import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Server, Monitor, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const useErrorCapture = () => {
  const [errors, setErrors] = useState({ frontend: [], backend: [] });

  useEffect(() => {
    console.log('ErrorLogs component mounted');

    const loadErrors = () => {
      const storedErrors = JSON.parse(localStorage.getItem('appErrors') || '{"frontend":[],"backend":[]}');
      console.log('Loaded errors from localStorage:', storedErrors);
      setErrors(storedErrors);
    };

    loadErrors();

    const intervalId = setInterval(loadErrors, 5000);

    const handleFrontendError = (event) => {
      console.log('Captured frontend error:', event);
      logError('frontend', event.error.message, event.error.stack);
    };

    window.addEventListener('error', handleFrontendError);
    window.addEventListener('unhandledrejection', (event) => {
      handleFrontendError({ error: event.reason });
    });

    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        console.log('Captured axios error:', error);
        logError('backend', error.response?.data?.message || error.message, error.stack);
        return Promise.reject(error);
      }
    );

    const logError = (type, message, stack) => {
      console.log('Logging error:', type, message, stack);
      const newError = {
        id: Date.now(),
        message,
        stack,
        timestamp: new Date().toISOString(),
      };
      const updatedErrors = JSON.parse(localStorage.getItem('appErrors') || '{"frontend":[],"backend":[]}');
      updatedErrors[type].push(newError);
      localStorage.setItem('appErrors', JSON.stringify(updatedErrors));
      setErrors(updatedErrors);
    };

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('error', handleFrontendError);
      window.removeEventListener('unhandledrejection', handleFrontendError);
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return errors;
};

const ErrorLogs = () => {
  const errors = useErrorCapture();
  const [activeTab, setActiveTab] = useState('frontend');
  const [expandedErrors, setExpandedErrors] = useState({});

  const toggleErrorExpansion = (id) => {
    setExpandedErrors(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const ErrorCard = ({ error }) => (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="mb-3 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
      <div 
        className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
        onClick={() => toggleErrorExpansion(error.id)}
      >
        <div className="flex min-w-0 items-center gap-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-danger-500 dark:text-danger-400" />
          <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{error.message}</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(error.timestamp).toLocaleString()}</span>
          {expandedErrors[error.id] ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>
      <AnimatePresence>
        {expandedErrors[error.id] && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-gray-600 dark:text-gray-400">{error.stack}</pre>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Error Logs</h2>
      <div className="segmented">
        <button
          onClick={() => setActiveTab('frontend')}
          className={`segmented-item ${activeTab === 'frontend' ? 'segmented-item-active' : ''}`}
        >
          <div className="flex items-center gap-1.5">
            <Monitor className="h-4 w-4" />
            <span>Frontend Errors ({errors.frontend.length})</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('backend')}
          className={`segmented-item ${activeTab === 'backend' ? 'segmented-item-active' : ''}`}
        >
          <div className="flex items-center gap-1.5">
            <Server className="h-4 w-4" />
            <span>Backend Errors ({errors.backend.length})</span>
          </div>
        </button>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {errors[activeTab].length > 0 ? (
            errors[activeTab].map(error => <ErrorCard key={error.id} error={error} />)
          ) : (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No {activeTab} errors captured.</p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ErrorLogs;